import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import API from "../../../util/API";

const MAX_CATEGORY_PATH_DEPTH = 2;
const AUTO_ASSIGN_BATCH_SIZE = 200;

const normalizeCategoryPath = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .slice(0, MAX_CATEGORY_PATH_DEPTH);

const makeCategoryOptionValue = (categoryId, categoryPath = []) => {
  const id = String(categoryId || "").trim();
  if (!id) return "";
  return `${id}::${encodeURIComponent(JSON.stringify(normalizeCategoryPath(categoryPath)))}`;
};

const parseCategoryOptionValue = (value) => {
  const text = String(value || "");
  if (!text) return { categoryId: "", categoryPath: [] };
  const [categoryId, encodedPath = ""] = text.split("::");
  try {
    return {
      categoryId: String(categoryId || "").trim(),
      categoryPath: normalizeCategoryPath(JSON.parse(decodeURIComponent(encodedPath || "%5B%5D"))),
    };
  } catch {
    return { categoryId: String(categoryId || "").trim(), categoryPath: [] };
  }
};

const buildCategoryOptions = (categories = []) => {
  const options = [{ value: "", categoryId: "", categoryPath: [], label: "Unassigned" }];

  const pushOption = (categoryId, rootName, path = []) => {
    const categoryPath = normalizeCategoryPath(path);
    options.push({
      value: makeCategoryOptionValue(categoryId, categoryPath),
      categoryId,
      categoryPath,
      label: categoryPath.length ? `${rootName} > ${categoryPath.join(" > ")}` : rootName,
    });
  };

  const walk = (root, nodes, path = []) => {
    if (!Array.isArray(nodes) || path.length >= MAX_CATEGORY_PATH_DEPTH) return;
    nodes.forEach((node) => {
      const nodeName = String(node?.name || "").trim();
      if (!nodeName) return;
      const nextPath = [...path, nodeName];
      pushOption(String(root?._id || ""), String(root?.name || ""), nextPath);
      walk(root, node?.subcategories, nextPath);
    });
  };

  (Array.isArray(categories) ? categories : []).forEach((category) => {
    const categoryId = String(category?._id || "").trim();
    const rootName = String(category?.name || "").trim();
    if (!categoryId || !rootName) return;
    pushOption(categoryId, rootName, []);
    walk(category, category?.subcategories, []);
  });

  return options;
};

const getProductTitle = (product) => String(product?.name || product?.productId || "Untitled product").trim();

const formatCount = (value) => Number(value || 0).toLocaleString();

const getLogTime = () =>
  new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const getCategoryLabel = (product) => {
  const name = String(product?.category?.name || "").trim();
  const path = normalizeCategoryPath(product?.categoryPath);
  if (!name && !path.length) return "Unassigned";
  if (!name) return path.join(" > ");
  return path.length ? `${name} > ${path.join(" > ")}` : name;
};

const getCategoryValue = (product) =>
  makeCategoryOptionValue(product?.categoryId || product?.category?._id || "", product?.categoryPath || []);

export default function CategorizedProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0 });
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [categoryValue, setCategoryValue] = useState("");
  const [showOnlyUnassigned, setShowOnlyUnassigned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoResult, setAutoResult] = useState(null);
  const [autoLogs, setAutoLogs] = useState([]);

  const categoryOptions = useMemo(() => buildCategoryOptions(categories), [categories]);

  const loadCategories = async () => {
    const res = await API.get("/category");
    setCategories(Array.isArray(res?.data) ? res.data : res?.data?.data || []);
  };

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const res = await API.get("/product/category/stats");
      const data = res?.data?.data || null;
      setStats(data);
      return data;
    } catch (error) {
      setStats(null);
      toast.error(error?.response?.data?.message || "Failed to load category statistics.");
      return null;
    } finally {
      setStatsLoading(false);
    }
  };

  const loadProducts = async ({ page = pagination.page, limit = pagination.limit, q = search } = {}) => {
    setLoading(true);
    try {
      const res = await API.get(
        `/product/admin/all?currency=USD&priceCountry=US&page=${page}&limit=${limit}${q ? `&search=${encodeURIComponent(q)}` : ""}`
      );
      setProducts(Array.isArray(res?.data?.data) ? res.data.data : []);
      setPagination(res?.data?.pagination || { page, limit, total: 0 });
      setSelectedIds([]);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load products.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
    loadStats();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts({ page: 1, q: search });
    }, search ? 350 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const visibleProducts = useMemo(() => {
    if (!showOnlyUnassigned) return products;
    return products.filter((product) => !product?.categoryId && !product?.category?._id);
  }, [products, showOnlyUnassigned]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allVisibleSelected =
    visibleProducts.length > 0 && visibleProducts.every((product) => selectedSet.has(String(product?.productId || "")));

  const toggleOne = (productId) => {
    const id = String(productId || "").trim();
    if (!id) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleVisible = () => {
    const ids = visibleProducts.map((product) => String(product?.productId || "").trim()).filter(Boolean);
    setSelectedIds((prev) => {
      if (allVisibleSelected) return prev.filter((id) => !ids.includes(id));
      return Array.from(new Set([...prev, ...ids]));
    });
  };

  const addAutoLog = (message, type = "info") => {
    setAutoLogs((prev) => [...prev, { id: `${Date.now()}-${prev.length}`, time: getLogTime(), message, type }].slice(-80));
  };

  const assignSelected = async () => {
    if (!selectedIds.length) return toast.error("Select at least one product.");
    const option = parseCategoryOptionValue(categoryValue);
    setSaving(true);
    try {
      const res = await API.post("/product/category/bulk-assign", {
        productIds: selectedIds,
        categoryId: option.categoryId || null,
        categoryPath: option.categoryPath,
      });
      toast.success(res?.data?.message || "Category assigned.");
      await loadProducts();
      await loadStats();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to assign category.");
    } finally {
      setSaving(false);
    }
  };

  const runAutoAssign = async () => {
    setAutoRunning(true);
    setAutoResult(null);
    setAutoLogs([]);
    try {
      const firstStats = await loadStats();
      const startingUnassigned = Number(firstStats?.unassignedProducts ?? stats?.unassignedProducts ?? 0);
      const totals = {
        scanned: 0,
        assigned: 0,
        skipped: 0,
        semanticAssigned: 0,
        fallbackAssigned: 0,
      };
      let batch = 0;

      addAutoLog(`Started auto assignment. Unassigned products: ${formatCount(startingUnassigned)}.`);

      while (true) {
        batch += 1;
        addAutoLog(`Batch ${batch} started. Processing up to ${formatCount(AUTO_ASSIGN_BATCH_SIZE)} products.`);
        const res = await API.post("/product/category/auto-assign", {
          limit: AUTO_ASSIGN_BATCH_SIZE,
          batchSize: AUTO_ASSIGN_BATCH_SIZE,
        });
        const data = res?.data?.data || {};
        totals.scanned += Number(data.scanned || 0);
        totals.assigned += Number(data.assigned || 0);
        totals.skipped += Number(data.skipped || 0);
        totals.semanticAssigned += Number(data.semanticAssigned || 0);
        totals.fallbackAssigned += Number(data.fallbackAssigned || 0);

        addAutoLog(
          `Batch ${batch} finished. Scanned ${formatCount(data.scanned)}, assigned ${formatCount(data.assigned)}, semantic ${formatCount(data.semanticAssigned)}, fallback ${formatCount(data.fallbackAssigned)}, skipped ${formatCount(data.skipped)}.`,
          Number(data.assigned || 0) ? "success" : "info"
        );

        const latestStats = await loadStats();
        const remaining = Number(latestStats?.unassignedProducts || 0);
        addAutoLog(`Remaining unassigned products: ${formatCount(remaining)}.`);

        if (!Number(data.scanned || 0) || !remaining || Number(data.scanned || 0) < AUTO_ASSIGN_BATCH_SIZE) break;
      }

      setAutoResult(totals);
      addAutoLog(
        `Completed. Total scanned ${formatCount(totals.scanned)}, assigned ${formatCount(totals.assigned)}, semantic ${formatCount(totals.semanticAssigned)}, fallback ${formatCount(totals.fallbackAssigned)}, skipped ${formatCount(totals.skipped)}.`,
        "success"
      );
      toast.success("Auto assignment finished.");
      await loadProducts();
    } catch (error) {
      addAutoLog(error?.response?.data?.message || "Auto assignment failed.", "error");
      toast.error(error?.response?.data?.message || "Failed to auto assign categories.");
    } finally {
      setAutoRunning(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(Number(pagination.total || 0) / Number(pagination.limit || 25)));
  const statsCards = [
    {
      label: "Total products",
      value: stats?.totalProducts,
      hint: "All products in catalog",
    },
    {
      label: "Unassigned products",
      value: stats?.unassignedProducts,
      hint: "Products with no category",
    },
    {
      label: "Missing category/subcategory",
      value: stats?.missingCategoryOrSubcategoryProducts,
      hint: "No category or parent category only",
    },
    {
      label: "Empty category/subcategory",
      value: stats?.emptyCategoryOptions,
      hint: "Available options with no products",
    },
  ];

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Categorized Products</h2>
          <p className="text-sm text-slate-600">Select products, assign a category, or auto-fill uncategorized products.</p>
        </div>
        <button
          type="button"
          onClick={runAutoAssign}
          disabled={autoRunning}
          className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-[#D2042D] disabled:opacity-60"
        >
          {autoRunning ? "Auto assigning..." : "Auto assign uncategorized"}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {statsCards.map((card) => (
          <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{card.label}</p>
            <p className="mt-2 text-2xl font-bold text-slate-900">
              {statsLoading ? "..." : formatCount(card.value)}
            </p>
            <p className="mt-1 text-xs text-slate-500">{card.hint}</p>
          </div>
        ))}
      </div>

      {autoResult ? (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Scanned {formatCount(autoResult.scanned)}, assigned {formatCount(autoResult.assigned)}, skipped {formatCount(autoResult.skipped)}.
          {" "}Semantic {formatCount(autoResult.semanticAssigned)}, fallback {formatCount(autoResult.fallbackAssigned)}.
        </div>
      ) : null}

      {(autoRunning || autoLogs.length > 0) ? (
        <div className="rounded-2xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Auto assignment log</p>
              <p className="text-xs text-slate-500">Batch progress and assignment summary</p>
            </div>
            {autoRunning ? (
              <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                Running
              </span>
            ) : (
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                Finished
              </span>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto px-4 py-3">
            {autoLogs.length ? (
              <div className="space-y-2">
                {autoLogs.map((log) => (
                  <div
                    key={log.id}
                    className={[
                      "rounded-xl border px-3 py-2 text-sm",
                      log.type === "error"
                        ? "border-rose-100 bg-rose-50 text-rose-800"
                        : log.type === "success"
                          ? "border-emerald-100 bg-emerald-50 text-emerald-800"
                          : "border-slate-100 bg-slate-50 text-slate-700",
                    ].join(" ")}
                  >
                    <span className="mr-2 font-mono text-xs text-slate-500">{log.time}</span>
                    {log.message}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Waiting for the first batch...</p>
            )}
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_1fr_auto_auto] lg:items-end">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-slate-600">Search products</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by title or product ID"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#D2042D] focus:ring-2 focus:ring-[#D2042D]/20"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-slate-600">Assign category</span>
            <select
              value={categoryValue}
              onChange={(event) => setCategoryValue(event.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#D2042D] focus:ring-2 focus:ring-[#D2042D]/20"
            >
              {categoryOptions.map((option) => (
                <option key={option.value || "unassigned"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={showOnlyUnassigned}
              onChange={(event) => setShowOnlyUnassigned(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]"
            />
            Unassigned only
          </label>

          <button
            type="button"
            onClick={assignSelected}
            disabled={saving || !selectedIds.length}
            className="rounded-2xl bg-[#D2042D] px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
          >
            {saving ? "Assigning..." : `Assign selected (${selectedIds.length})`}
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="text-sm font-semibold text-slate-800">
            {loading ? "Loading products..." : `${visibleProducts.length} products on this page`}
          </p>
          <select
            value={pagination.limit}
            onChange={(event) => loadProducts({ page: 1, limit: Number(event.target.value) })}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          >
            <option value={25}>25 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleVisible}
                    className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]"
                  />
                </th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Current category</th>
                <th className="px-4 py-3">Reach</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleProducts.map((product) => {
                const productId = String(product?.productId || "").trim();
                return (
                  <tr key={productId || product?._id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedSet.has(productId)}
                        onChange={() => toggleOne(productId)}
                        className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-xl truncate font-semibold text-slate-900">{getProductTitle(product)}</div>
                      <div className="mt-1 font-mono text-xs text-slate-500">ID: {productId || "-"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={[
                          "inline-flex max-w-md rounded-full border px-2.5 py-1 text-xs font-semibold",
                          getCategoryValue(product)
                            ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                            : "border-amber-100 bg-amber-50 text-amber-700",
                        ].join(" ")}
                      >
                        <span className="truncate">{getCategoryLabel(product)}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {Number(product?.availableCountriesCount || 0)} countries
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!loading && !visibleProducts.length ? (
          <div className="px-4 py-12 text-center text-sm text-slate-500">No products found.</div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-sm text-slate-600">
          Page <span className="font-semibold text-slate-900">{pagination.page}</span> of{" "}
          <span className="font-semibold text-slate-900">{totalPages}</span>
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={pagination.page <= 1 || loading}
            onClick={() => loadProducts({ page: Math.max(1, Number(pagination.page || 1) - 1) })}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={pagination.page >= totalPages || loading}
            onClick={() => loadProducts({ page: Math.min(totalPages, Number(pagination.page || 1) + 1) })}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
